import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  format,
  formatDistanceToNow,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
} from "date-fns";
import { fr } from "date-fns/locale";
import { motion } from "motion/react";
import {
  Users,
  Calendar,
  Wallet,
  Clock,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { chefQueries } from "@/domain/chef/chef.query";
import { bookingQueries } from "@/domain/booking/booking.query";
import { BookingStatuses, PaymentStatuses } from "@hometoq/backend/enums";
import { getIpxImageUrl } from "@/lib/image";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/admin/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data: chefs = [] } = useQuery(chefQueries.adminChefs());
  const { data: bookingsResponse } = useQuery(bookingQueries.adminBookings());
  const bookings = bookingsResponse?.data ?? [];

  // ───── Stats agrégées
  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);

    const verifiedChefs = chefs.filter((c: any) => c.accountVerified).length;
    const pendingChefs = chefs.length - verifiedChefs;

    const bookingsThisMonth = bookings.filter((b: any) =>
      isWithinInterval(new Date(b.date), {
        start: monthStart,
        end: monthEnd,
      }),
    );
    const revenueThisMonth = bookingsThisMonth
      .filter((b: any) => b.paymentStatus === PaymentStatuses.COMPLETED)
      .reduce((sum: number, b: any) => sum + Number(b.price ?? 0), 0);

    const pendingBookings = bookings.filter(
      (b: any) => b.bookingStatus === BookingStatuses.PENDING,
    ).length;
    const upcomingBookings = bookings.filter(
      (b: any) =>
        b.bookingStatus === BookingStatuses.ACCEPTED &&
        new Date(b.date) >= now,
    ).length;

    return {
      verifiedChefs,
      pendingChefs,
      totalChefs: chefs.length,
      bookingsThisMonth: bookingsThisMonth.length,
      revenueThisMonth: revenueThisMonth / 100,
      pendingBookings,
      upcomingBookings,
    };
  }, [chefs, bookings]);

  // Pending chefs (sorted by createdAt asc — anciens d'abord)
  const pendingChefsList = useMemo(() => {
    return [...chefs]
      .filter((c: any) => !c.accountVerified)
      .sort(
        (a: any, b: any) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
      .slice(0, 5);
  }, [chefs]);

  // Recent bookings (5 plus récents)
  const recentBookings = useMemo(() => {
    return [...bookings]
      .sort(
        (a: any, b: any) =>
          new Date(b.date).getTime() - new Date(a.date).getTime(),
      )
      .slice(0, 5);
  }, [bookings]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* ─────────  Header  ───────── */}
      <header>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/80">
            Espace admin
          </span>
          <span className="w-1 h-1 rounded-full bg-primary" />
          <span className="text-[11px] text-muted-foreground capitalize">
            {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
          </span>
        </div>
        <h1 className="font-h1 text-dark-blue">Vue d'ensemble</h1>
        <p className="font-small text-muted-foreground mt-1.5 max-w-xl">
          Pilotez la communauté HomeToq depuis ce tableau de bord — chefs,
          réservations, revenus, alertes.
        </p>
      </header>

      {/* ─────────  Alert principale (chefs à valider)  ───────── */}
      {stats.pendingChefs > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl bg-primary text-white p-5 lg:p-6 flex items-center gap-4"
        >
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/20 blur-3xl pointer-events-none" />
          <div className="relative w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0">
            <AlertCircle className="w-6 h-6" />
          </div>
          <div className="relative flex-1 min-w-0">
            <p className="font-button-extra-small uppercase tracking-wider text-white/80 mb-0.5">
              Action requise
            </p>
            <p className="font-h4 leading-tight">
              {stats.pendingChefs} candidature
              {stats.pendingChefs > 1 ? "s" : ""} chef à examiner
            </p>
            <p className="font-small text-white/75 mt-0.5">
              Validez les profils en attente pour les rendre visibles aux
              clients.
            </p>
          </div>
          <Link
            to="/dashboard/admin/chefs"
            className="relative shrink-0 inline-flex items-center gap-1.5 bg-white text-primary font-button-small px-4 py-2.5 rounded-xl hover:bg-cream/90 transition-colors"
          >
            Examiner
            <ArrowRight size={14} />
          </Link>
        </motion.div>
      )}

      {/* ─────────  KPI tiles  ───────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPITile
          label="Chefs total"
          value={stats.totalChefs}
          subtitle={`${stats.verifiedChefs} vérifiés · ${stats.pendingChefs} en attente`}
          icon={Users}
          tone="neutral"
        />
        <KPITile
          label="Réservations ce mois"
          value={stats.bookingsThisMonth}
          subtitle={`${stats.upcomingBookings} à venir au total`}
          icon={Calendar}
          tone="info"
        />
        <KPITile
          label="Revenu ce mois"
          value={`${stats.revenueThisMonth.toFixed(0)} €`}
          subtitle="Paiements complétés"
          icon={Wallet}
          tone="success"
          big
        />
        <KPITile
          label="En attente"
          value={stats.pendingBookings}
          subtitle="Réservations à statuer"
          icon={Clock}
          tone="warn"
        />
      </div>

      {/* ─────────  Two-column : pending chefs + recent bookings  ───────── */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Pending chefs */}
        <Section
          title="Candidatures à valider"
          subtitle={
            stats.pendingChefs === 0
              ? "Vous êtes à jour"
              : `${stats.pendingChefs} en file d'attente`
          }
          icon={Sparkles}
          link={{ to: "/dashboard/admin/chefs", label: "Tout voir" }}
        >
          {pendingChefsList.length === 0 ? (
            <EmptyRow
              icon={CheckCircle2}
              text="Toutes les candidatures ont été traitées"
              tone="success"
            />
          ) : (
            <ul className="divide-y divide-border">
              {pendingChefsList.map((chef: any) => (
                <li key={chef.id}>
                  <Link
                    to="/dashboard/admin/chefs"
                    className="flex items-center gap-3 px-4 py-3 hover:bg-cream/30 transition-colors group"
                  >
                    <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
                      <AvatarImage
                        src={
                          chef.avatar
                            ? getIpxImageUrl(chef.avatar, 80)
                            : "/ipx/f_webp,q_75,w_80/images/find_chef_avatar.webp"
                        }
                        alt={chef.firstName}
                      />
                      <AvatarFallback className="bg-primary/10 text-primary font-bold text-xs">
                        {chef.firstName?.[0]}
                        {chef.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-button-small text-dark-blue truncate">
                        {chef.firstName} {chef.lastName}
                      </p>
                      <p className="font-small text-muted-foreground truncate">
                        {chef.specialisation || "Spécialisation non renseignée"}
                        {chef.workingCity && ` · ${chef.workingCity}`}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="font-button-extra-small uppercase tracking-wider text-primary">
                        À valider
                      </span>
                      {chef.createdAt && (
                        <span className="font-small text-muted-foreground/80">
                          {formatDistanceToNow(new Date(chef.createdAt), {
                            locale: fr,
                            addSuffix: true,
                          })}
                        </span>
                      )}
                    </div>
                    <ChevronRight
                      size={14}
                      className="text-muted-foreground transition-transform group-hover:translate-x-0.5 shrink-0"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Recent bookings */}
        <Section
          title="Dernières réservations"
          subtitle={
            recentBookings.length > 0
              ? `${stats.bookingsThisMonth} ce mois`
              : "Aucune pour l'instant"
          }
          icon={TrendingUp}
          link={{ to: "/dashboard/admin/reservations", label: "Tout voir" }}
        >
          {recentBookings.length === 0 ? (
            <EmptyRow
              icon={Calendar}
              text="Les nouvelles réservations apparaîtront ici"
              tone="neutral"
            />
          ) : (
            <ul className="divide-y divide-border">
              {recentBookings.map((booking: any) => (
                <li key={booking.id}>
                  <Link
                    to="/dashboard/admin/reservations"
                    className="flex items-center gap-3 px-4 py-3 hover:bg-cream/30 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-cream/60 flex items-center justify-center shrink-0">
                      <Calendar size={16} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-button-small text-dark-blue truncate">
                        {booking.menu?.name ?? "Menu sur-mesure"}
                        {booking.isCustom && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-primary font-bold">
                            · sur-mesure
                          </span>
                        )}
                      </p>
                      <p className="font-small text-muted-foreground truncate">
                        Chef {booking.chef?.firstName} ·{" "}
                        {booking.bookingContact?.firstName}{" "}
                        {booking.bookingContact?.lastName}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                      <span className="font-button-small text-dark-blue tabular-nums">
                        {(Number(booking.price) / 100).toFixed(0)} €
                      </span>
                      <span className="font-small text-muted-foreground tabular-nums">
                        {format(new Date(booking.date), "dd/MM/yy")}
                      </span>
                    </div>
                    <ChevronRight
                      size={14}
                      className="text-muted-foreground transition-transform group-hover:translate-x-0.5 shrink-0"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Sub-components
// ─────────────────────────────────────────────────────────────────────────

function KPITile({
  label,
  value,
  subtitle,
  icon: Icon,
  tone,
  big = false,
}: {
  label: string;
  value: string | number;
  subtitle?: string;
  icon: typeof Users;
  tone: "neutral" | "info" | "success" | "warn";
  big?: boolean;
}) {
  const toneClasses = {
    neutral: "bg-cream/50 text-dark-blue",
    info: "bg-blue-100 text-blue-700",
    success: "bg-emerald-100 text-emerald-700",
    warn: "bg-yellow-100 text-yellow-700",
  } as const;

  return (
    <div className="bg-white rounded-2xl border border-border p-4 lg:p-5">
      <div className="flex items-center justify-between mb-3">
        <span
          className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center",
            toneClasses[tone],
          )}
        >
          <Icon size={16} />
        </span>
      </div>
      <p
        className={cn(
          "font-bold text-dark-blue tabular-nums leading-none",
          big ? "text-3xl" : "text-2xl",
        )}
      >
        {value}
      </p>
      <p className="font-small text-muted-foreground mt-1.5">{label}</p>
      {subtitle && (
        <p className="font-small text-muted-foreground/70 mt-0.5 truncate">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function Section({
  title,
  subtitle,
  icon: Icon,
  link,
  children,
}: {
  title: string;
  subtitle?: string;
  icon: typeof Users;
  link?: { to: string; label: string };
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-2xl border border-border overflow-hidden flex flex-col">
      <header className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <Icon size={14} />
          </span>
          <div className="min-w-0">
            <p className="font-button-small text-dark-blue leading-tight">
              {title}
            </p>
            {subtitle && (
              <p className="font-small text-muted-foreground leading-tight mt-0.5">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {link && (
          <Link
            to={link.to}
            className="font-button-small text-primary hover:underline shrink-0 inline-flex items-center gap-1"
          >
            {link.label}
            <ChevronRight size={12} />
          </Link>
        )}
      </header>
      <div className="flex-1">{children}</div>
    </section>
  );
}

function EmptyRow({
  icon: Icon,
  text,
  tone,
}: {
  icon: typeof Calendar;
  text: string;
  tone: "neutral" | "success";
}) {
  return (
    <div className="px-5 py-8 text-center">
      <Icon
        className={cn(
          "w-7 h-7 mx-auto mb-2",
          tone === "success" ? "text-emerald-500" : "text-muted-foreground/50",
        )}
      />
      <p className="font-small text-muted-foreground">{text}</p>
    </div>
  );
}
